/**
 * Complementary Products — Hello Retail first, Shopify fallback.
 *
 * Renders HR recommendations directly as mini-cards with click tracking.
 * Falls back to the server-rendered Shopify product cards if HR fails.
 * Supports "list" (with show more/less toggle), "carousel", and "grid" layouts.
 */
(function () {
  const INITIAL_VISIBLE = 3;

  /**
   * Renders a single mini-card HTML string from HR product data.
   */
  function renderMiniCard(product, trackingKey, index) {
    const title = product.title || '';
    const url = product.url || '#';
    const imgUrl = product.imgUrl || '';
    const brand = product.brand || '';
    const variantId = product.extraData?.id || product['extraData.id'] || '';
    const isMedicin = product.extraData?.isMedicin === 'true' || product.extraData?.isMedicin === true;
    const price = product.price ? parseFloat(product.price) : 0;
    const oldPrice = product.oldPrice ? parseFloat(product.oldPrice) : 0;
    const isOnSale = oldPrice > 0 && oldPrice > price;
    const escapedTitle = title.replace(/"/g, '&quot;');

    const fmt = (val) =>
      val.toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr';

    let priceHtml = '';
    if (isOnSale) {
      const savingAmount = Math.round(oldPrice - price);
      const discountPct = ((oldPrice - price) / oldPrice) * 100;
      const sparBadge = (discountPct >= 5 && savingAmount >= 5)
        ? `<span class="mc-price-spar">Spar ${savingAmount} kr</span>`
        : '';
      priceHtml = `
        <span class="price price--on-sale">
          <span class="mc-price-top"><span class="price__was">Vejl. ${fmt(oldPrice)}</span></span>
          <span class="mc-price-row"><span class="price__current">${fmt(price)}</span>${sparBadge}</span>
        </span>`;
    } else if (price > 0) {
      priceHtml = `<span class="price"><span class="price__current">${fmt(price)}</span></span>`;
    }

    const trackAttr = trackingKey
      ? `onclick="var h=window.hrq||[];h.push(['trackClick','${trackingKey}'])"`
      : '';

    const hiddenClass = index >= INITIAL_VISIBLE ? ' complp-card--hidden' : '';

    return `
      <div class="mini-card${hiddenClass}" data-complp-index="${index}">
        <a href="${url}" class="mini-card__image-link" ${trackAttr}>
          <div class="mini-card__image">
            ${imgUrl
        ? `<img src="${imgUrl}" alt="${escapedTitle}" loading="lazy" width="72" height="72">`
        : `<div class="mini-card__placeholder">Intet billede</div>`}
          </div>
        </a>
        <div class="mini-card__right">
          <a href="${url}" class="mini-card__info" ${trackAttr}>
            ${brand ? `<span class="mini-card__brand">${brand}</span>` : ''}
            <span class="mini-card__title">${title}</span>
          </a>
          <div class="mini-card__bottom">
            <div class="mini-card__prices">
              ${priceHtml}
            </div>
            ${isMedicin ? `
              <div class="mini-card__add">
                <a href="${url}" class="btn btn--primary mini-card__cart-btn mini-card__cart-btn--pdp" aria-label="Gå til ${`${brand} ${escapedTitle}`.trim()}" ${trackAttr}>
                  <span class="mini-card__cart-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                  </span>
                </a>
              </div>
            ` : variantId ? `
              <div class="mini-card__add">
                <product-form>
                  <div class="alert mb-8 bg-error-bg text-error-text js-form-error text-start" role="alert" hidden></div>
                  <form method="post" action="/cart/add" class="js-product-form" accept-charset="UTF-8" enctype="multipart/form-data" novalidate="novalidate">
                    <input type="hidden" name="form_type" value="product">
                    <input type="hidden" name="utf8" value="✓">
                    <input type="hidden" name="id" value="${variantId}">
                    <button class="btn btn--primary mini-card__cart-btn" name="add" aria-haspopup="dialog"
                      data-add-to-cart-text="Læg i kurv"
                      ${trackingKey ? `data-tracking-key="${trackingKey}"` : ''}
                      ${trackingKey ? `onclick="if(this.dataset.trackingKey){var h=window.hrq||[];h.push(['trackClick',this.dataset.trackingKey])}"` : ''}
                    >
                      <span class="mini-card__cart-icon">
                        <span class="visually-hidden">Læg i kurv</span>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon">
                          <path d="M12.12 20.44H5.6V9.56h12.8v3.73c.06.4.4.69.8.7.44 0 .8-.35.8-.8v-4.5a.792.792 0 0 0-.8-.69H17V6.5C16.9 4 14.7 2 12 2S7 4.09 7 6.67V8H4.71c-.4.04-.71.37-.71.78v12.53a.8.8 0 0 0 .8.69h7.43c.38-.06.67-.39.67-.78 0-.43-.35-.78-.78-.78ZM8.66 6.67c0-1.72 1.49-3.11 3.33-3.11s3.33 1.39 3.33 3.11V8H8.65V6.67Z"></path>
                          <path d="M20 17.25h-2.4v-2.5a.817.817 0 0 0-.8-.7c-.44 0-.8.36-.8.8v2.4h-2.5c-.4.06-.7.4-.7.8 0 .44.36.8.8.8H16v2.5c.06.4.4.7.8.7.44 0 .8-.36.8-.8v-2.4h2.5c.4-.06.69-.4.7-.8 0-.44-.35-.8-.8-.8Z"></path>
                        </svg>
                      </span>
                    </button>
                    <input type="hidden" name="product-id" value="${product.productNumber || ''}">
                  </form>
                </product-form>
              </div>
            ` : ''}
          </div>
        </div>
      </div>`;
  }

  /**
   * Initialises the show more / less toggle.
   */
  function initToggle(wrapper, container) {
    const toggleBtn = wrapper.querySelector('.js-complp-toggle');
    if (!toggleBtn) return;

    const hiddenCards = container.querySelectorAll('.complp-card--hidden');
    if (hiddenCards.length === 0) {
      toggleBtn.style.display = 'none';
      return;
    }

    toggleBtn.style.display = '';
    let expanded = false;
    toggleBtn.onclick = () => {
      expanded = !expanded;
      hiddenCards.forEach(card => {
        card.classList.toggle('complp-card--hidden', !expanded);
      });
      toggleBtn.textContent = expanded ? 'Vis færre' : 'Vis flere';
    };
  }

  /**
   * Main init — runs once per wrapper.
   */
  async function init(wrapper) {
    if (wrapper.dataset.complpInit === 'true') return;
    wrapper.dataset.complpInit = 'true';

    const productId = wrapper.dataset.productId;
    const layout = wrapper.dataset.layout || 'list';
    if (!productId) return;

    // Wait for HelloRetailService
    if (!window.HelloRetailService) {
      await new Promise((resolve) => {
        let retries = 0;
        const interval = setInterval(() => {
          retries++;
          if (window.HelloRetailService || retries > 20) {
            clearInterval(interval);
            resolve();
          }
        }, 250);
      });
    }

    if (!window.HelloRetailService) {
      showFallback(wrapper, layout);
      return;
    }

    try {
      const result = await window.HelloRetailService.fetchRecommendations({
        awsKey: 'product-page-complementary',
        productIds: [productId],
        count: 10,
        filters: { inStock: "true" }
      });

      const products = result?.products;
      const trackingKey = result?.trackingKey || null;

      if (!products || products.length === 0) {
        showFallback(wrapper, layout);
        return;
      }

      // Render HR cards
      const hrContainer = wrapper.querySelector('.js-complp-hr');
      if (!hrContainer) return;

      hrContainer.innerHTML = products.map((p, i) => renderMiniCard(p, trackingKey, i)).join('');
      hrContainer.style.display = '';

      // Show toggle for list layout
      if (layout === 'list' && products.length > INITIAL_VISIBLE) {
        initToggle(wrapper, hrContainer);
      }

    } catch (err) {
      console.error('Complementary products HR error:', err);
      showFallback(wrapper, layout);
    }
  }

  /**
   * Shows Shopify fallback — waits for product-recommendations to fetch if needed.
   */
  function showFallback(wrapper, layout) {
    const recEl = wrapper.querySelector('product-recommendations');
    if (!recEl) return;

    const tryShow = () => {
      const fallback = recEl.querySelector('.js-complp-fallback');
      if (fallback && fallback.children.length > 0) {
        fallback.style.display = '';

        // Apply show more/less for list layout
        if (layout === 'list') {
          const cards = fallback.querySelectorAll('.mini-card');
          cards.forEach((card, i) => {
            if (i >= INITIAL_VISIBLE) card.classList.add('complp-card--hidden');
          });
          if (cards.length > INITIAL_VISIBLE) {
            initToggle(wrapper, fallback);
          }
        }
        return true;
      }
      return false;
    };

    // Try immediately — the recommendations may already be rendered
    if (tryShow()) return;

    // Otherwise observe the product-recommendations element for content changes
    const observer = new MutationObserver(() => {
      if (tryShow()) observer.disconnect();
    });
    observer.observe(recEl, { childList: true, subtree: true });

    // Timeout: stop observing after 10s
    setTimeout(() => observer.disconnect(), 10000);
  }

  // Init on page ready
  function initAll() {
    document.querySelectorAll('[data-complp-wrapper]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Support Shopify theme editor
  document.addEventListener('shopify:section:load', initAll);
})();
